import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { selectAuth } from '../../store/auth/auth.reducers';
import { selectEnrollmentRequired } from '../../store/twoFactor/two-factor.reducers';
import { captureReturnUrl } from '../utils/return-url';

/**
 * Shared implementation for the two mirror-image forced-2FA-enrollment guards. Both read the single
 * `selectEnrollmentRequired` fact and decide a binary outcome; they differ only in which value of the
 * flag is allowed through and where the other value is redirected. Factored out (like
 * {@link resolvePostAuthTarget}) so the gating logic can't drift between the /app child guard and the
 * /auth/enroll guard.
 *
 * @param allowWhenRequired the value of `enrollmentRequired` for which navigation is permitted; the
 *   opposite value redirects to `redirectTo`.
 * @param redirectTo the route to bounce to when the flag doesn't match `allowWhenRequired`.
 * @param captureAttempted when true, capture the attempted URL before redirecting so it can be restored
 *   after the flow completes. Set on the /app child guard: a forced-enrollment user cold-loading (or
 *   in-app-navigating to) a bookmarked `/app/...` deep-link is authenticated, so CheckAuthService does
 *   NOT capture it, and without this the URL is lost and `EnrollComponent.finish()` falls back to the
 *   home page. The capture is STAMPED with the enrolling user's id (the forced-enrollment user is
 *   authenticated, so we know it): every other authenticated capture site owner-stamps, so a DIFFERENT
 *   user signing into the same tab afterwards (per-tab sessionStorage survives logout) can't have this
 *   deep-link restored for them. `captureReturnUrl` no-ops for non-restorable URLs, so it is harmless
 *   on the mirror guard.
 */
export function enrollmentFlagGuard(allowWhenRequired: boolean, redirectTo: string, captureAttempted = false): CanActivateFn {
  return (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const store = inject(Store);
    const router = inject(Router);
    return combineLatest([
      store.pipe(select(selectEnrollmentRequired)),
      store.pipe(select(selectAuth)),
    ]).pipe(
      take(1),
      map(([required, auth]) => {
        if (required === allowWhenRequired) {
          return true;
        }
        if (captureAttempted) {
          captureReturnUrl(state.url, auth.userId);
        }
        return router.parseUrl(redirectTo);
      }),
    );
  };
}
